import { shouldPanHorizontally } from '@components/util/horizontalWheelPan';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import classNames from 'classnames';
import { forwardRef, MutableRefObject, Ref, useCallback, useLayoutEffect, useRef } from 'react';
import css from './GradientScrollDiv.module.scss';

/**
 * GradientScrollDiv - A scrollable container with gradient fade effects at the edges
 *
 * Shows gradient masks at top/bottom (or left/right) edges that fade in/out based on scroll position,
 * indicating to users when there's more content to scroll.
 *
 * CRITICAL USAGE NOTES - Read this to avoid common pitfalls:
 *
 * 1. DUAL WRAPPER ARCHITECTURE:
 *    - This component creates TWO nested divs:
 *      * OUTER div: Uses the `className` prop - receives layout/sizing properties
 *      * INNER div: Uses the `innerDivClassName` prop - receives content/padding styles
 *    - The inner div is the actual scrolling element (overflow-y: scroll from .contentInner)
 *
 * 2. CRITICAL STYLING PATTERN (for flexbox layouts):
 *    - OUTER wrapper (className) MUST have:
 *      * `flex: 1` - Makes it grow to fill available space
 *      * `min-height: 0` - CRITICAL! Allows flex items to shrink (enables scrolling in flex containers)
 *      * `max-height: XXXpx` - Sets the scrollable area height constraint
 *      * NO `overflow` properties (GradientScrollDiv handles this internally)
 *
 *    - INNER wrapper (innerDivClassName) should have:
 *      * Content-specific styles (padding, etc.)
 *      * Same `max-height` as outer wrapper
 *      * NO `overflow` properties (already handled by .contentInner)
 *
 * 3. HORIZONTAL ROWS AND THE WHEEL:
 *    - With `horizontalScrolling`, a vertical wheel pans the row — but only while the row has
 *      room left that way. At either end the wheel goes to the page instead, so a row can
 *      never trap the page's scroll. See `shouldPanHorizontally`, which owns that decision and
 *      is unit-tested; an earlier unconditional version of this was a scroll trap (`3b1f8007`).
 *    - Native horizontal gestures (`deltaX`) are left to the browser.
 *    - `horizontalArrows` adds a click target at each end that has more behind it, for the
 *      mouse users a hidden scrollbar leaves with nothing to aim at.
 *
 *    The fades and arrows track overflow through a `ResizeObserver` plus a re-measure after
 *    every render, not just `scroll` — so a row that overflows on first layout, or fills from
 *    a fetch, shows its fade without waiting for the user to scroll first.
 *
 * 4. COMMON TRAPS TO AVOID:
 *    - ❌ DON'T put `overflow-y: auto` on your custom classes (conflicts with internal scrolling)
 *    - ❌ DON'T forget `min-height: 0` in flex layouts (prevents scrolling from working)
 *    - ❌ DON'T put height constraints only on outer OR inner - needs both
 *    - ❌ DON'T use `height: 100%` without flex properties in flex layouts
 *
 * 5. LAYERING WITH FIXED HEADERS/FOOTERS:
 *    - If you have fixed headers/footers adjacent to GradientScrollDiv:
 *      * Parent container needs: `display: flex; flex-direction: column`
 *      * Header/footer needs: `flex-shrink: 0` (prevent shrinking)
 *      * Header/footer may need: `position: relative; z-index: 1; background-color: <bg>` (prevent content from showing through)
 *      * GradientScrollDiv wrapper needs: `flex: 1; min-height: 0` (fill remaining space)
 *
 * EXAMPLE 1 - Popover with fixed header/footer (like MediaDataGrid filters):
 * ```tsx
 * // SCSS:
 * .popoverContent {
 *   display: flex;
 *   flex-direction: column;
 *   max-height: calc(80vh - 100px);
 * }
 * .header {
 *   flex-shrink: 0;
 *   padding: 0 20px;
 * }
 * .scrollContainer {
 *   flex: 1;
 *   min-height: 0;
 *   max-height: 400px;
 * }
 * .scrollInner {
 *   padding: 0 20px;
 *   max-height: 400px;
 * }
 * .footer {
 *   flex-shrink: 0;
 *   position: relative;
 *   z-index: 1;
 *   background-color: #1a1a1c;
 * }
 *
 * // TSX:
 * <div className={css.popoverContent}>
 *   <div className={css.header}>FILTERS</div>
 *   <GradientScrollDiv
 *     className={css.scrollContainer}
 *     innerDivClassName={css.scrollInner}
 *   >
 *     <div>...scrollable content...</div>
 *   </GradientScrollDiv>
 *   <div className={css.footer}>Reset filters</div>
 * </div>
 * ```
 *
 * EXAMPLE 2 - Simple full-page scroll (like Settings):
 * ```tsx
 * // SCSS:
 * .settingsContent {
 *   // GradientScrollDiv handles all scrolling internally
 * }
 *
 * // TSX:
 * <GradientScrollDiv className={css.settingsContent}>
 *   <div>...content...</div>
 * </GradientScrollDiv>
 * ```
 *
 * EXAMPLE 3 - Menu with constrained height (like LibrarySwitch):
 * ```tsx
 * // SCSS:
 * .scroll {
 *   max-height: 380px;
 *   overflow: hidden;
 * }
 * .gradientInner {
 *   max-height: 380px;
 * }
 *
 * // TSX:
 * <div className={css.scroll}>
 *   <GradientScrollDiv innerDivClassName={css.gradientInner}>
 *     <div>...items...</div>
 *   </GradientScrollDiv>
 * </div>
 * ```
 */
interface GradientScrollDivProps {
  children: React.ReactNode;
  className?: string;
  innerDivClassName?: string;
  horizontalScrolling?: boolean;
  /**
   * Show a nudge arrow over each end that has more content behind it. Opt-in, and only read
   * with `horizontalScrolling` — rows that are dragged rather than stepped through (the
   * MomentMerge timeline) do not want a button sitting over their content.
   *
   * Worth it on a row that hides its scrollbar: the fade says there is more, the wheel can
   * reach it, but neither is something a mouse user can see and click.
   */
  horizontalArrows?: boolean;
  style?: React.CSSProperties;
  innerStyle?: React.CSSProperties;
}

// Utility to merge multiple refs (callback and/or RefObject) into a single callback ref
function mergeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(value);
      } else if (typeof ref === 'object' && 'current' in ref) {
        (ref as MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export const GradientScrollDiv = forwardRef<HTMLDivElement, GradientScrollDivProps>(
  (
    {
      children,
      className = '',
      innerDivClassName = '',
      horizontalScrolling = false,
      horizontalArrows = false,
      style,
      innerStyle,
    },
    ref,
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const startOpacityRef = useRef(0);
    const endOpacityRef = useRef(0);
    const startArrowRef = useRef<HTMLButtonElement>(null);
    const endArrowRef = useRef<HTMLButtonElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const applyMaskImage = useCallback(
      (container: HTMLDivElement) => {
        const start = startOpacityRef.current;
        const end = endOpacityRef.current;
        const dir = horizontalScrolling ? 'to right' : 'to bottom';
        let mask: string;

        if (start > 0 && end > 0) {
          mask = `linear-gradient(${dir}, transparent 0%, black ${start * 30}px, black calc(100% - ${end * 30}px), transparent 100%)`;
        } else if (start > 0) {
          mask = `linear-gradient(${dir}, transparent 0%, black ${start * 30}px)`;
        } else if (end > 0) {
          mask = `linear-gradient(${dir}, black calc(100% - ${end * 30}px), transparent 100%)`;
        } else {
          container.style.maskImage = '';
          container.style.webkitMaskImage = '';
          return;
        }

        container.style.maskImage = mask;
        container.style.webkitMaskImage = mask;
      },
      [horizontalScrolling],
    );

    const updateGradients = useCallback(() => {
      const container = contentRef.current;
      if (!container) return;

      const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } =
        container;
      const fadeDistance = 100; // Distance over which the gradient fades in

      let newStart: number;
      let newEnd: number;

      if (horizontalScrolling) {
        newStart = Math.min(scrollLeft / fadeDistance, 1);
        const distanceFromRight = scrollWidth - (scrollLeft + clientWidth);
        newEnd = Math.min(distanceFromRight / fadeDistance, 1);
      } else {
        newStart = Math.min(scrollTop / fadeDistance, 1);
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
        newEnd = Math.min(distanceFromBottom / fadeDistance, 1);
      }

      // Quantize to avoid unnecessary DOM writes (steps of ~3%)
      newStart = Math.round(newStart * 30) / 30;
      newEnd = Math.round(newEnd * 30) / 30;

      // Toggled on the DOM rather than through state on purpose: this runs on every scroll
      // event, and `3af79623` moved the mask off React for exactly that reason. An arrow is
      // shown when its side has overflow, which is the same fact the fade already encodes.
      //
      // Deliberately OUTSIDE the change check below, and not a bug worth re-introducing: the
      // arrow buttons sit after the scroller in the tree, so their refs are still null on the
      // first call, made from the scroller's own ref callback. Gated together with the mask,
      // that first call would record the new values while the `?.` quietly skipped the arrows,
      // and every later call would then early-exit as "nothing changed" — leaving the arrows
      // hidden until an actual scroll moved the numbers. `classList.toggle` with an explicit
      // force argument is idempotent, so running it unconditionally costs nothing.
      startArrowRef.current?.classList.toggle(css.arrowHidden, newStart <= 0);
      endArrowRef.current?.classList.toggle(css.arrowHidden, newEnd <= 0);

      // The mask is a real style write, so it keeps its change check.
      if (newStart !== startOpacityRef.current || newEnd !== endOpacityRef.current) {
        startOpacityRef.current = newStart;
        endOpacityRef.current = newEnd;
        applyMaskImage(container);
      }
    }, [horizontalScrolling, applyMaskImage]);

    /**
     * A vertical wheel pans a horizontal row — but only while the row still has somewhere to go
     * that way. At either end the event is left alone and the page scrolls as usual.
     *
     * A handler like this existed before and was removed in `3b1f8007`: it called
     * `preventDefault` on every wheel event, so resting the cursor over a horizontal row (the
     * Workspace favourites, the MomentMerge timeline) trapped the page's vertical scroll even
     * when the row had no horizontal room left. The bounds check below is what makes it safe to
     * have back — the row consumes the wheel only when it can act on it. Without some handler
     * a mouse cannot reach the overflow at all, because rows that hide their scrollbar are left
     * with a fade advertising content and no way to get to it.
     *
     * A real horizontal gesture (`deltaX`, a trackpad swipe) is never touched: the browser
     * already scrolls the row, and intercepting it too would move it twice as far.
     */
    const handleWheel = useCallback((event: WheelEvent) => {
      const container = contentRef.current;
      if (!container || !shouldPanHorizontally(event, container)) return;
      event.preventDefault();
      container.scrollLeft += event.deltaY;
    }, []);

    // A little under a full screen, so one landmark carries over and the reader keeps their
    // place — the same reason PageDown overlaps a line.
    const nudge = useCallback((direction: -1 | 1) => {
      const container = contentRef.current;
      if (!container) return;
      container.scrollBy({ left: direction * container.clientWidth * 0.8, behavior: 'smooth' });
    }, []);

    const setContentRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (node) {
          contentRef.current = node;
          node.addEventListener('scroll', updateGradients);
          // `passive: false`, or the `preventDefault` above is ignored.
          if (horizontalScrolling) node.addEventListener('wheel', handleWheel, { passive: false });

          // Overflow appears and disappears without anyone scrolling, and a `scroll` listener
          // alone never hears about it. The first measurement is the case that bit: the row is
          // measured before its children have laid out, reads no overflow, and leaves the fade
          // and the arrows hidden until the user happens to scroll — at which point they pop in,
          // which is not a thing that should need a scroll to become true.
          resizeObserverRef.current?.disconnect();
          const observer = new ResizeObserver(updateGradients);
          observer.observe(node);
          resizeObserverRef.current = observer;

          updateGradients();
        } else if (contentRef.current) {
          contentRef.current.removeEventListener('scroll', updateGradients);
          contentRef.current.removeEventListener('wheel', handleWheel);
          resizeObserverRef.current?.disconnect();
          resizeObserverRef.current = null;
        }
      },
      [updateGradients, horizontalScrolling, handleWheel],
    );

    // The other half of the same problem: when children are added or removed the scroller's own
    // box does not change, so the `ResizeObserver` above stays quiet while `scrollWidth` moves
    // under it — a row filled from a fetch is the usual way in. Re-measuring after every render
    // covers it; `updateGradients` exits early when nothing moved, so the cost is a few reads.
    useLayoutEffect(() => {
      updateGradients();
    });

    const combinedRef = mergeRefs<HTMLDivElement>(setContentRef, ref);

    return (
      <div className={classNames(css.content, className)} style={style}>
        <div
          ref={combinedRef}
          className={classNames(
            css.contentInner,
            {
              [css.horizontal]: horizontalScrolling,
            },
            innerDivClassName,
          )}
          style={innerStyle}
        >
          {children}
        </div>
        {horizontalScrolling && horizontalArrows && (
          <>
            {/*
              Out of the tab order and hidden from assistive tech: they scroll, they do not
              navigate. Tabbing through the row's own controls already scrolls each into view,
              and a screen reader reaches the content directly — so these would be two stops
              that announce nothing and do nothing for either. Same call as `ThreadLanes`'
              lane buttons.
            */}
            <button
              ref={startArrowRef}
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className={classNames(css.arrow, css.arrowStart, css.arrowHidden)}
              onClick={() => nudge(-1)}
            >
              <ChevronLeftRounded fontSize="small" />
            </button>
            <button
              ref={endArrowRef}
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className={classNames(css.arrow, css.arrowEnd, css.arrowHidden)}
              onClick={() => nudge(1)}
            >
              <ChevronRightRounded fontSize="small" />
            </button>
          </>
        )}
      </div>
    );
  },
);
